'use client';

import ClickableDiv from '@shared/components/elements/clickable';
import { ClearSpinner } from '@shared/components/layout/spinner';
import { reloadCachedDiamondMedia } from '@shared/lib/overnight/actions';
import { decodeVendorVideoUrl, fetch360Data, parse360Data } from '@shared/lib/overnight/iframe-api';
import { mergeFrames } from '@shared/lib/overnight/image360-utils';
import { Diamond360FrameMeta, InStockDiamond } from '@shared/lib/overnight/types';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { IoIosPlay } from 'react-icons/io';
import { Md360 } from 'react-icons/md';
import DiamondMainImage from './diamond-main-image';

const MAX_FRAME_DEPTH = 5; // Can be up to 7, but 5 is enough. 0 is 4 frames, 5 is 4*2**5 = 128 frames

export default function Diamond360({
  diamond,
  show360 = false,
}: {
  diamond: InStockDiamond;
  show360?: boolean;
}) {
  const [currentDiamond, setCurrentDiamond] = useState<InStockDiamond>(diamond);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [readyFrames, setReadyFrames] = useState<string[]>([]);
  const [frameCollections, setFrameCollections] = useState<string[][]>(
    Array.from({ length: MAX_FRAME_DEPTH }, () => [])
  );
  const [isLoading, setIsLoading] = useState(false);
  const [frameMeta, setFrameMeta] = useState<Diamond360FrameMeta | undefined>(undefined);

  const [hasInteracted, setHasInteracted] = useState(false);

  const [preloadedImages, setPreloadedImages] = useState<HTMLImageElement[]>([]);
  const mouseMoveTimeout = useRef<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      const decodeAndFetchVideoUrl = async () => {
        try {
          setIsLoading(true);
          let { image360Url, videoUrl } = diamond;
          // Reload the cached media if the diamond has no 360 frames or videos
          if (!diamond.image360Url && !diamond.videoUrl) {
            const media = await reloadCachedDiamondMedia(diamond.lotNumber);
            if (media) {
              ({ image360Url, videoUrl } = media);
              setCurrentDiamond({
                ...diamond,
                image360Url: media.image360Url,
                videoUrl: media.videoUrl,
                imageUrl: media.imageUrl,
              });
            }
          }
          // If there is a pre-cached 360 frames, use it
          if (image360Url) {
            const mergedFrames = await fetch(image360Url);
            const mergedFramesJson = await mergedFrames.json();
            setReadyFrames(mergedFramesJson);
            setIsLoading(false);
          } else if (videoUrl) {
            setVideoUrl(videoUrl);
          } else {
            // No pre-cached 360 frames and videos, conducting decoding on the fly.
            const { image360Url, videoUrl, referer, image360Data } = await decodeVendorVideoUrl(
              diamond.vendorVideoUrl
            );
            if (image360Url || image360Data) {
              const { error, result } = image360Url
                ? await fetch360Data(image360Url, referer)
                : await parse360Data(image360Data!);

              if (error) {
                console.warn('Error fetching 360 data:', error);
                setError('Failed to load diamond 360.');
              } else {
                setFrameMeta(result!.metaData);
                setFrameCollections(result!.frameCollection);
              }
              setIsLoading(false);
            } else if (videoUrl) {
              setVideoUrl(
                `/api/proxy?url=${encodeURIComponent(videoUrl!)}&referer=${encodeURIComponent(
                  referer || ''
                )}` || null
              );
            } else {
              setError('Failed to load diamond 360.');
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.error('Error fetching diamond image/video:', error);
          setError('Failed to load diamond 360.');
        }
      };
      decodeAndFetchVideoUrl();
    }
  };

  const [currentIndex, setCurrentIndex] = useState(Math.floor(readyFrames.length / 2));
  const [isAutoplaying, setIsAutoplaying] = useState(true);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const image360Ref = useRef<HTMLDivElement>(null);

  // Compose the frames into image360s
  useEffect(() => {
    const composeReadyFrames = () => {
      if (!frameMeta) {
        return;
      }
      const readyFrameIndex = frameCollections.findIndex((frames) => frames.length === 0) - 1;
      if (readyFrameIndex < 0) {
        return;
      }
      let mergedFrames: string[] = [];
      for (let i = 0; i <= readyFrameIndex; i++) {
        mergedFrames = mergeFrames(mergedFrames, frameCollections[i]!, frameMeta?.scramble?.[i]);
      }
      setReadyFrames(mergedFrames);
      setIsLoading(false);
    };
    composeReadyFrames();
  }, [frameCollections, frameMeta, setReadyFrames, setIsLoading]);

  useEffect(() => {
    if (!isAutoplaying || readyFrames.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // If we've reached the middle after going through 0, go back playing
        if (prevIndex === Math.floor(readyFrames.length / 2) - 1 && direction === 1) {
          setDirection(-1);
          return prevIndex - 1;
        }
        // If we've reached the beginning after going through 0, go forward playing
        else if (prevIndex === Math.floor(readyFrames.length / 2) && direction === -1) {
          setDirection(1);
          return prevIndex + 1;
        } else if (prevIndex === readyFrames.length - 1 && direction === 1) {
          setIsAutoplaying(false);
          return 0;
        }
        // Otherwise continue in forward direction
        else {
          return (prevIndex + direction + readyFrames.length) % readyFrames.length;
        }
      });
    }, 3200 / readyFrames.length);

    // Stop at middle when we've gone through the full rotation
    if (currentIndex === 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [
    currentIndex,
    direction,
    readyFrames.length,
    isAutoplaying,
    setCurrentIndex,
    setDirection,
    setIsAutoplaying,
  ]);

  // Preload images when image360s changes
  useEffect(() => {
    const preloadImages = async () => {
      const images = await Promise.all(
        readyFrames.map(async (base64) => {
          const img = document.createElement('img');
          img.src = `data:image/jpeg;base64,${base64}`;
          await new Promise((resolve) => {
            img.onload = resolve;
          });
          return img;
        })
      );
      setPreloadedImages(images);
    };
    preloadImages();
  }, [readyFrames, setPreloadedImages]);

  const updateCurrentIndex = (x: number, width: number) => {
    // Map x position to image index with smoother transition
    const index =
      (Math.min(Math.max(Math.floor((x / width) * readyFrames.length), 0), readyFrames.length - 1) +
        readyFrames.length / 2) %
      readyFrames.length;
    setCurrentIndex(index);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!image360Ref.current) return;

    setIsAutoplaying(false);
    const rect = image360Ref.current!.getBoundingClientRect();
    const x = rect.right - e.clientX;
    const width = rect.width;
    updateCurrentIndex(x, width);
  };

  const handleMouseEnter = () => {
    setIsAutoplaying(false);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    setIsAutoplaying(true);
    const rect = image360Ref.current!.getBoundingClientRect();
    setDirection(e.clientX > rect.left + rect.width / 2 ? 1 : -1);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mouseMoveTimeout.current) {
        clearTimeout(mouseMoveTimeout.current);
      }
    };
  }, []);

  const [startX, setStartX] = useState<number | null>(null);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!image360Ref.current) return;
    const touch = event.touches[0];
    if (touch) {
      setStartX(touch.clientX);
      setIsAutoplaying(false);
    }
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!image360Ref.current) return;
    const touch = event.touches[0];
    if (startX !== null && touch) {
      setIsAutoplaying(false);
      const rect = image360Ref.current!.getBoundingClientRect();
      const x = startX - touch.clientX;
      const width = rect.width;

      updateCurrentIndex(x, width);
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!image360Ref.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const touch = event.changedTouches[0];
    if (
      touch &&
      (touch.clientX < rect.left ||
        touch.clientX > rect.right ||
        touch.clientY < rect.top ||
        touch.clientY > rect.bottom)
    ) {
      // If touch ends outside the rectangle, start animation
      setIsAutoplaying(true);
    }
    setStartX(null);
  };

  if (!diamond.vendorVideoUrl && !diamond.image360Url && !diamond.videoUrl) {
    return null;
  }

  return (
    <ClickableDiv
      className="@container group relative aspect-square w-full overflow-hidden bg-white"
      onMouseEnter={(e) => {
        if (mouseMoveTimeout.current) {
          clearTimeout(mouseMoveTimeout.current);
        }
        mouseMoveTimeout.current = setTimeout(() => {
          handleInteraction();
        }, 300); // 300ms debounce delay
      }}
      onMouseLeave={() => {
        if (mouseMoveTimeout.current) {
          clearTimeout(mouseMoveTimeout.current);
        }
      }}
      onTouchStart={handleInteraction}
    >
      {readyFrames.length > 0 ? (
        <>
          <div className="absolute inset-0 flex cursor-grabbing items-center justify-center">
            <div
              ref={image360Ref}
              className="relative h-full w-full"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {readyFrames.map((base64, index) => (
                <Image
                  key={index}
                  src={`data:image/jpeg;base64,${base64}`}
                  alt={`360 image ${index}`}
                  className={`absolute h-full w-full object-cover object-center ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                  width={frameMeta?.width || 400}
                  height={frameMeta?.height || 400}
                  loading="eager"
                />
              ))}
            </div>
          </div>

          <div className="absolute bottom-4 left-4 flex flex-col items-center gap-0">
            <Thumbnail360 />
          </div>
        </>
      ) : videoUrl ? (
        <>
          <video
            ref={(el) => {
              if (el) {
                el.addEventListener('loadedmetadata', () => {
                  // Set initial state when video loads
                  el.currentTime = 0;
                  // Only play automatically if the cursor is not over the video
                  if (!el.parentElement?.matches(':hover')) {
                    el.play();
                  }
                  setIsLoading(false);
                });
              }
            }}
            src={videoUrl}
            muted
            loop={false}
            className="h-full w-full cursor-grabbing object-cover object-center"
            onMouseMove={(e) => {
              const video = e.currentTarget;
              const rect = video.getBoundingClientRect();
              const x = rect.right - e.clientX;
              const percentage = (x / rect.width + 0.5) % 1.0;

              // Seek to position based on mouse x coordinate
              if (video.duration) {
                video.currentTime = percentage * video.duration;
              }
              video.pause();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.pause();
            }}
            onMouseLeave={(e) => {
              const video = e.currentTarget;
              // Play from current position to end
              video.play();

              // When video ends, reset to beginning
              const onEnded = () => {
                video.currentTime = 0;
                video.pause();
                video.removeEventListener('ended', onEnded);
              };

              video.addEventListener('ended', onEnded);
            }}
            onTouchStart={(e) => {
              e.currentTarget.pause();
            }}
            onTouchMove={(e) => {
              if (e.touches.length > 0) {
                const video = e.currentTarget;
                const rect = video.getBoundingClientRect();
                const x = e.touches[0]!.clientX - rect.left;
                const percentage = Math.max(0, Math.min(1, x / rect.width));

                if (video.duration) {
                  video.currentTime = percentage * video.duration;
                }
              }
            }}
            onTouchEnd={(e) => {
              const video = e.currentTarget;
              video.play();

              const onEnded = () => {
                video.currentTime = 0;
                video.pause();
                video.removeEventListener('ended', onEnded);
              };

              video.addEventListener('ended', onEnded);
            }}
            preload="auto"
          />

          <div className="absolute bottom-4 left-4 flex flex-col items-center gap-0">
            <Thumbnail360 />
          </div>
        </>
      ) : (
        <>
          <DiamondMainImage diamond={currentDiamond} />
          {!error && !isLoading && (
            <div className="bg-bd-light/40 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md p-4">
              <IoIosPlay className="text-bd-secondary text-6xl" />
            </div>
          )}
          {error && (
            <p className="bg-bd-light/40 absolute left-1/2 top-4 -translate-x-1/2 whitespace-nowrap rounded-md p-1 text-center text-sm font-normal text-red-800">
              {error}
            </p>
          )}
          {diamond.vendorVideoUrl && show360 && (
            <div className="absolute bottom-4 left-4 flex flex-col items-center gap-0">
              <Thumbnail360 />
            </div>
          )}
        </>
      )}

      {isLoading && <ClearSpinner className="h-10 w-10 border-2" />}
    </ClickableDiv>
  );
}

export function Thumbnail360() {
  return (
    <>
      <p className="text-bd-primary text-lg font-normal uppercase">360</p>
      <Md360 className="text-bd-primary -mt-3 text-4xl" />
    </>
  );
}
